import { useState } from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import { Menu, Text, TextInput } from "react-native-paper";

import type { QuestionComponentProps } from "./types";

import { colors } from "@/lib/theme";

const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
];

function buildYearOptions(): string[] {
    const current = new Date().getFullYear();
    const years: string[] = [];
    for (let y = current; y >= current - 30; y--) {
        years.push(String(y));
    }
    return years;
}

const YEARS = buildYearOptions();

/**
 * Month + Year picker for date questions.
 * Stores value as "Month YYYY" (e.g., "March 2026").
 */
export function DateQuestion({ value, onChange, readOnly, onFocus }: QuestionComponentProps) {
    const [monthOpen, setMonthOpen] = useState(false);
    const [yearOpen, setYearOpen] = useState(false);

    // Parse existing value
    const parts = (value ?? "").split(" ");
    const selectedMonth = MONTHS.includes(parts[0]) ? parts[0] : "";
    const selectedYear = parts[1] && /^\d{4}$/.test(parts[1]) ? parts[1] : "";

    function handleMonth(month: string) {
        setMonthOpen(false);
        const newValue = selectedYear ? `${month} ${selectedYear}` : month;
        onChange(newValue);
    }

    function handleYear(year: string) {
        setYearOpen(false);
        const newValue = selectedMonth ? `${selectedMonth} ${year}` : year;
        onChange(newValue);
    }

    if (readOnly) {
        return (
            <Text style={styles.readOnly} variant="bodyMedium">
                {value || "—"}
            </Text>
        );
    }

    return (
        <View style={styles.row}>
            <Menu
                visible={monthOpen}
                onDismiss={() => setMonthOpen(false)}
                anchor={
                    <Pressable
                        onPress={() => {
                            onFocus?.();
                            setMonthOpen(true);
                        }}
                        style={styles.selector}
                    >
                        <Text style={selectedMonth ? styles.selectedText : styles.placeholder}>
                            {selectedMonth || "Month"}
                        </Text>
                        <Text style={styles.chevron}>▼</Text>
                    </Pressable>
                }
            >
                <ScrollView style={styles.menuScroll} keyboardShouldPersistTaps="handled">
                    {MONTHS.map((m) => (
                        <Menu.Item key={m} onPress={() => handleMonth(m)} title={m} />
                    ))}
                </ScrollView>
            </Menu>

            <Menu
                visible={yearOpen}
                onDismiss={() => setYearOpen(false)}
                anchor={
                    <Pressable
                        onPress={() => {
                            onFocus?.();
                            setYearOpen(true);
                        }}
                        style={styles.selector}
                    >
                        <Text style={selectedYear ? styles.selectedText : styles.placeholder}>
                            {selectedYear || "Year"}
                        </Text>
                        <Text style={styles.chevron}>▼</Text>
                    </Pressable>
                }
            >
                <ScrollView style={styles.menuScroll} keyboardShouldPersistTaps="handled">
                    {YEARS.map((y) => (
                        <Menu.Item key={y} onPress={() => handleYear(y)} title={y} />
                    ))}
                </ScrollView>
            </Menu>
        </View>
    );
}

export function TimeQuestion({ value, onChange, readOnly, onFocus }: QuestionComponentProps) {
    return (
        <TextInput
            mode="outlined"
            value={value ?? ""}
            onChangeText={onChange}
            disabled={readOnly}
            placeholder="HH:MM"
            onFocus={onFocus}
            style={styles.timeInput}
            dense
        />
    );
}

const styles = StyleSheet.create({
    row: {
        flexDirection: "row",
        gap: 12,
        alignItems: "center"
    },
    // Cap the open menu so the long year list (e.g. 31 years) scrolls instead of
    // overflowing the screen. Paper's Menu only adds its own scroll when an async
    // measureInWindow check fires, which is unreliable on web (PWA) — without this
    // the first/top item (the current year) gets clipped off-screen on first open
    // and only appears after re-opening. Mirrors MenuSelect for the same reason.
    menuScroll: {
        maxHeight: 300
    },
    selector: {
        flexDirection: "row",
        alignItems: "center",
        borderWidth: 1,
        borderColor: colors.gray300,
        borderRadius: 8,
        paddingHorizontal: 14,
        paddingVertical: 10,
        backgroundColor: "#fff",
        minWidth: 130
    },
    placeholder: {
        color: colors.gray400,
        flex: 1
    },
    selectedText: {
        color: colors.gray800,
        flex: 1
    },
    chevron: {
        fontSize: 10,
        color: colors.gray400,
        marginLeft: 6
    },
    readOnly: {
        color: colors.gray600,
        paddingVertical: 8
    },
    timeInput: {
        backgroundColor: "#fff",
        maxWidth: 200
    }
});
